import React from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  Receipt,
  Assignment,
  Assessment,
  People,
  ShoppingCart,
  Inventory,
} from '@mui/icons-material';
import InvoicePage from './InvoicePage';
import CustomerPage from './CustomerPage';
import CustomerFormPage from './CustomerFormPage';
import SalesOrderPage from './SalesOrderPage';
import ProductPage from './ProductPage';
import ProductWorkspacePage from './ProductWorkspacePage';
import Product360Page from './Product360Page';
import UomMasterPage from './UomMasterPage';
import OpportunitiesPage from './OpportunitiesPage';
import OpportunityFormPage from './OpportunityFormPage';
import QuotationsPage from './QuotationsPage';
import QuotationFormPage from './QuotationFormPage';
import SalesReportsPage from './SalesReportsPage';
import OrderFulfillmentControl from './OrderFulfillmentControl';
import CustomerEnquiryPage from './CustomerEnquiryPage';
import CustomerEnquiryFormPage from './CustomerEnquiryFormPage';
import CustomerEnquiryDetailPage from './CustomerEnquiryDetailPage';
import SalesContractPage from './SalesContractPage';
import SalesContractFormPage from './SalesContractFormPage';
import SalesContractDetailPage from './SalesContractDetailPage';
import DeliveryPage from './DeliveryPage';
import DeliveryDetailPage from './DeliveryDetailPage';
import SalesOrderDetailPage from './SalesOrderDetailPage';
import SalesReturnPage from './SalesReturnPage';
import SalesReturnFormPage from './SalesReturnFormPage';
import SalesReturnDetailPage from './SalesReturnDetailPage';
import PriceListsPage from './PriceListsPage';
import PriceListFormPage from './PriceListFormPage';

const SalesOverview: React.FC = () => {
  const navigate = useNavigate();

  const salesModules = [
    {
      title: 'Product Management',
      description: 'Manage products with HSN codes and pricing',
      icon: <Inventory />,
      path: '/sales/products',
      count: 'Manage products',
      color: 'primary',
    },
    {
      title: 'Customer Management',
      description: 'Manage customer database with GSTIN validation',
      icon: <People />,
      path: '/sales/customers',
      count: '150 customers',
      color: 'secondary',
    },
    {
      title: 'Sales Orders',
      description: 'Create and track sales orders with GST calculations',
      icon: <ShoppingCart />,
      path: '/sales/orders',
      count: '45 orders',
      color: 'success',
    },
    {
      title: 'GST Invoices',
      description: 'Generate GST-compliant invoices with Indian format',
      icon: <Receipt />,
      path: '/sales/invoices',
      count: '32 invoices',
      color: 'info',
    },
    {
      title: 'Quotations',
      description: 'Create and manage price quotations',
      icon: <Assignment />,
      path: '/sales/quotations',
      count: '15 pending',
      color: 'warning',
    },
    {
      title: 'Sales Opportunities',
      description: 'Manage sales leads and opportunities',
      icon: <TrendingUp />,
      path: '/sales/opportunities',
      count: '25 active',
      color: 'secondary',
    },
    {
      title: 'Sales Reports',
      description: 'Analyze sales performance and GST reporting',
      icon: <Assessment />,
      path: '/sales/reports',
      count: '8 reports',
      color: 'error',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
        Sales Management - GST Enabled
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Complete sales management with GST compliance, GSTIN validation, and Indian tax calculations.
      </Typography>

      <Grid container spacing={3}>
        {salesModules.map((module, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: `${module.color}.main`, mr: 2, fontSize: 32 }}>
                    {module.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="h2">
                      {module.title}
                    </Typography>
                    <Chip 
                      label={module.count} 
                      size="small" 
                      color={module.color as any}
                      variant="outlined" 
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {module.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button 
                  size="small" 
                  color={module.color as any}
                  onClick={() => navigate(module.path)}
                  sx={{ fontWeight: 'bold' }}
                >
                  Open Module
                </Button>
                <Button size="small" variant="outlined">
                  Quick View
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// SalesInvoices component is now replaced by InvoicePage

const SalesPage: React.FC = () => {
  return (
    <Routes>
      <Route index element={<OrderFulfillmentControl />} />
      <Route path="overview" element={<SalesOverview />} />
      <Route path="products" element={<ProductPage />} />
      <Route path="products/new" element={<ProductWorkspacePage />} />
      <Route path="products/:id/edit" element={<ProductWorkspacePage />} />
      <Route path="products/:id/360" element={<Product360Page />} />
      <Route path="uom-master" element={<UomMasterPage />} />
      <Route path="customers" element={<CustomerPage />} />
      <Route path="customers/new" element={<CustomerFormPage />} />
      <Route path="customers/:id/edit" element={<CustomerFormPage />} />
      <Route path="orders" element={<SalesOrderPage />} />
      <Route path="orders/:id" element={<SalesOrderDetailPage />} />
      <Route path="opportunities" element={<OpportunitiesPage />} />
      <Route path="opportunities/new" element={<OpportunityFormPage />} />
      <Route path="opportunities/:id/edit" element={<OpportunityFormPage />} />
      <Route path="quotations" element={<QuotationsPage />} />
      <Route path="quotations/new" element={<QuotationFormPage />} />
      <Route path="quotations/:id/edit" element={<QuotationFormPage />} />
      <Route path="invoices/*" element={<InvoicePage />} />
      <Route path="reports" element={<SalesReportsPage />} />
      <Route path="enquiries" element={<CustomerEnquiryPage />} />
      <Route path="enquiries/new" element={<CustomerEnquiryFormPage />} />
      <Route path="enquiries/:id/edit" element={<CustomerEnquiryFormPage />} />
      <Route path="enquiries/:id" element={<CustomerEnquiryDetailPage />} />
      <Route path="contracts" element={<SalesContractPage />} />
      <Route path="contracts/new" element={<SalesContractFormPage />} />
      <Route path="contracts/:id/edit" element={<SalesContractFormPage />} />
      <Route path="contracts/:id" element={<SalesContractDetailPage />} />
      <Route path="deliveries" element={<DeliveryPage />} />
      <Route path="deliveries/:id" element={<DeliveryDetailPage />} />
      <Route path="returns" element={<SalesReturnPage />} />
      <Route path="returns/new" element={<SalesReturnFormPage />} />
      <Route path="returns/:id" element={<SalesReturnDetailPage />} />
      <Route path="price-lists" element={<PriceListsPage />} />
      <Route path="price-lists/new" element={<PriceListFormPage />} />
      <Route path="price-lists/:id/edit" element={<PriceListFormPage />} />
    </Routes>
  );
};

export default SalesPage;